import React, { useState, useEffect, useRef } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import chatService from '@/services/chatService';
import './ChatWindow.css';
import { assetUrl } from '@/utils/assetUrl';
import { encodeClickPayload } from '@/utils/clickProtocol';
const headerLogoIcon = assetUrl('/assets/header-logo.svg');
const headerLogoEnIcon = assetUrl('/assets/header-logo-en.svg');
const refreshIcon = assetUrl('/assets/refresh.svg');

const ChatWindow = ({ onClose, isWidget = false, lang = 'ar', dir = 'rtl' }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [agentState, setAgentState] = useState('listening'); // Track agent state
  const [copyFeedback, setCopyFeedback] = useState(false); // Track copy feedback
  const messageInputRef = useRef(null);

  // Translations based on language
  const t = {
    connectionError: lang === 'ar' 
      ? 'عذراً، فشل الاتصال بخدمة الدردشة. يرجى المحاولة مرة أخرى.'
      : 'Sorry, failed to connect to the chat service. Please try again.',
    sendError: lang === 'ar'
      ? 'عذراً، فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.'
      : 'Sorry, failed to send the message. Please try again.',
    newSession: lang === 'ar' ? 'بدء محادثة جديدة' : 'Start new conversation',
    minimizeChat: lang === 'ar' ? 'تصغير المحادثة' : 'Minimize chat',
    logoAlt: lang === 'ar' ? 'وزارة البيئة والمياه والزراعة' : 'Ministry of Environment, Water and Agriculture',
    refreshAlt: lang === 'ar' ? 'تحديث' : 'Refresh',
    copyMessages: lang === 'ar' ? 'نسخ المحادثة' : 'Copy conversation',
    copied: lang === 'ar' ? 'تم النسخ!' : 'Copied!',
    poweredBy: lang === 'ar' ? 'الإطلاق التجريبي' : 'Beta Launch',
  };

  // Handler to copy all messages as JSON
  const handleCopyMessages = async () => {
    // Filter out system messages and format for export
    const exportMessages = messages
      .filter(msg => msg.sender !== 'system')
      .map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
        timestamp: msg.timestamp,
      }));
    
    const jsonString = JSON.stringify(exportMessages, null, 2);
    
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopyFeedback(true);
      console.log('📋 Messages copied to clipboard:', exportMessages.length, 'messages');
      
      // Reset feedback after 2 seconds
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (error) {
      console.error('❌ Failed to copy messages:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = jsonString;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    }
  };

  // Handler to start a new session
  const handleNewSession = async () => {
    console.log('🆕 [ChatWindow] User requested new session');
    setMessages([]); // Clear UI immediately
    setIsLoading(false);
    setIsConnecting(true);
    
    try {
      const result = await chatService.startNewSession();
      if (result.success) {
        console.log('✅ [ChatWindow] New session started successfully');
      }
    } catch (error) {
      console.error('❌ [ChatWindow] Failed to start new session:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  // Initialize LiveKit connection on mount
  useEffect(() => {
    let unsubscribeMessages = null;
    let unsubscribeSessionReset = null;
    let unsubscribeAgentState = null;
    let isMounted = true;
    let isFirstConnection = true; // Track if this is the first connection

    const initializeChat = async () => {
      try {
        setIsConnecting(true);
        
        // Set language before connecting (determines which agent token to use)
        chatService.setLanguage(lang);
        
        // IMPORTANT: Register message listener FIRST, before connection
        // This ensures we capture any early messages that arrive during connection
        unsubscribeMessages = chatService.onMessage((message) => {
          console.log('🎯 [ChatWindow] Received message in component:', message);
          console.log('🎯 [ChatWindow] Component mounted:', isMounted);
          if (isMounted) {
            console.log('🎯 [ChatWindow] Updating messages state');
            setMessages(prev => {
              console.log('🎯 [ChatWindow] Previous messages:', prev.length);
              const newMessages = [...prev, message];
              console.log('🎯 [ChatWindow] New messages:', newMessages.length);
              return newMessages;
            });
            // Don't set isLoading to false here - let agent state handle it
          } else {
            console.warn('⚠️ [ChatWindow] Component not mounted, skipping state update');
          }
        });
        console.log('✅ Message listener registered BEFORE connection');
        
        // Listen for session reset events (new LiveKit session)
        // This should NOT clear messages when first connecting, only on manual reset
        unsubscribeSessionReset = chatService.onSessionReset((sessionInfo) => {
          console.log('🆕 [ChatWindow] Session reset event received:', sessionInfo);
          console.log('🆕 [ChatWindow] Is first connection:', isFirstConnection);
          
          // Only clear if this is NOT the first connection
          if (isMounted && !isFirstConnection) {
            console.log('🗑️ [ChatWindow] Clearing messages for new session');
            setMessages([]); // Clear all messages for new session
            setIsLoading(false);
            setAgentState('listening'); // Reset agent state
          } else {
            console.log('ℹ️ [ChatWindow] First connection, keeping initial messages');
            isFirstConnection = false; // Next reset should clear messages
          }
        });
        
        // Listen for agent state changes
        unsubscribeAgentState = chatService.onAgentStateChange((state) => {
          console.log('🤖 [ChatWindow] Agent state changed to:', state);
          if (isMounted) {
            setAgentState(state);
            // When agent finishes speaking/thinking, stop loading
            if (state === 'listening') {
              setIsLoading(false);
            }
          }
        });
        
        // Initialize and connect to LiveKit
        const result = await chatService.connect();
        
        // Check if component is still mounted before updating state
        if (!isMounted) return;
        
        if (result.success) {
          console.log('✅ Chat service connected');
        } else {
          console.error('❌ Failed to connect to chat service');
          // Show error message
          setMessages(prev => [...prev, {
            id: Date.now(),
            text: t.connectionError,
            sender: 'system',
            timestamp: new Date().toISOString(),
          }]);
        }
      } catch (error) {
        console.error('❌ Chat initialization error:', error);
      } finally {
        if (isMounted) {
          setIsConnecting(false);
        }
      }
    };

    initializeChat();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (unsubscribeMessages) {
        unsubscribeMessages();
      }
      if (unsubscribeSessionReset) {
        unsubscribeSessionReset();
      }
      if (unsubscribeAgentState) {
        unsubscribeAgentState();
      }
      // IMPORTANT: Don't disconnect here!
      // The connection should persist across component mounts/unmounts
      // Only disconnect when the user explicitly closes the app/tab
      console.log('🔄 ChatWindow unmounting, keeping connection alive');
    };
  }, []); // Empty dependency array - run once on mount

  // Shared dispatcher: render `displayText` in the chat as the user bubble,
  // but actually transmit `wireText` to the agent. For plain typed messages
  // the two are identical; for click protocol v2 taps the wire payload is the
  // encoded `__click__|...` string while the bubble shows just the label.
  const dispatchUserTurn = async (displayText, wireText = displayText) => {
    const userMessage = {
      id: Date.now(),
      text: displayText,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Focus the input field (especially important after clicking on clickable options)
    messageInputRef.current?.focus();

    try {
      const result = await chatService.sendMessage(wireText);

      if (!result.success) {
        throw new Error('Failed to send message');
      }

      console.log('✅ Message sent successfully');
    } catch (error) {
      console.error('❌ Failed to send message:', error);

      setMessages(prev => [...prev, {
        id: Date.now(),
        text: t.sendError,
        sender: 'system',
        timestamp: new Date().toISOString(),
      }]);

      setIsLoading(false);
    }
  };

  const handleSendMessage = async (text) => {
    // NOTE: free-form user text is sent verbatim. The reserved `__click__|`
    // prefix is only ever produced by `handleClickableClick` below.
    await dispatchUserTurn(text);
  };

  // Click protocol v2 handler: receives { label, action, id } from Message.jsx.
  // - Attributed clicks (action+id present) -> show label, send encoded payload.
  // - Legacy bare <click>label</click> (no action/id) -> behave like before:
  //   send the literal label as a normal user message.
  const handleClickableClick = async (payload) => {
    if (!payload) return;
    const { label, action, id } = payload;

    if (action && id) {
      const wire = encodeClickPayload({ action, id, label });
      await dispatchUserTurn(label, wire);
      return;
    }

    await handleSendMessage(label);
  };

  return (
    <div 
      className={`chat-window ${isWidget ? 'chat-window-widget' : 'chat-window-app'} ${dir === 'rtl' ? 'chat-window-rtl' : 'chat-window-ltr'}`} 
      dir={dir}
      style={{ direction: dir, textAlign: dir === 'rtl' ? 'right' : 'left' }}
    >
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="chat-header-logo">
            <img src={lang === 'en' ? headerLogoEnIcon : headerLogoIcon} alt={t.logoAlt} className="header-logo-icon" />
          </div>
        </div>
        <div className="chat-header-actions">
          {!isWidget && (
            <button 
              className={`chat-copy-button ${copyFeedback ? 'copied' : ''}`}
              onClick={handleCopyMessages}
              aria-label={t.copyMessages}
              title={copyFeedback ? t.copied : t.copyMessages}
              disabled={messages.length === 0}
            >
              {copyFeedback ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="currentColor" strokeWidth="2"/>
                </svg>
              )}
            </button>
          )}
          <button 
            className="chat-new-session-button" 
            onClick={handleNewSession}
            aria-label={t.newSession}
            title={t.newSession}
          >
            <img src={refreshIcon} alt={t.refreshAlt} className="refresh-icon" />
          </button>
          {isWidget && (
            <button 
              className="chat-close-button" 
              onClick={() => {
                console.log('🔴 Minimize button clicked!');
                console.log('🔴 onClose function:', onClose);
                if (onClose && typeof onClose === 'function') {
                  onClose();
                } else {
                  console.error('❌ onClose is not a function!', onClose);
                }
              }}
              aria-label={t.minimizeChat}
              title={t.minimizeChat}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>
      
      <MessageList
        messages={messages}
        isLoading={isLoading}
        isConnecting={isConnecting}
        agentState={agentState}
        onClickableTextClick={handleClickableClick}
        lang={lang}
        isWidget={isWidget}
      />
      
      <MessageInput 
        ref={messageInputRef}
        onSendMessage={handleSendMessage} 
        disabled={isLoading || isConnecting}
        lang={lang}
      />

      <div className="chat-footer" dir={dir}>
        <span className="chat-footer-text">
          {t.poweredBy}
        </span>
      </div>
    </div>
  );
};

export default ChatWindow;
